/**
 * Tenant login action creator.
 *
 * Posts `{identifier, password, tenantSlug}` to `/api/v1/auth/login`. The 200 body is
 * a tagged union (discriminator `type`):
 *   A) `type: "success"` — `{accessToken, refreshToken?, user, …}`. We set the
 *      token + kick the proactive timer and return `{kind: "success", snapshot}`.
 *   B) `type: "otp_required"` — `{requiresOtp: true, otpSessionToken, ttl,
 *      channel}`. No access token yet; we return `{kind: "otp-required", …}` so
 *      the UI transitions to the OTP step.
 *   C) `type: "totp_required"` — `{requiresTotp: true, totpSessionToken, ttl}`.
 *      The account has an authenticator app enrolled. No access token yet; we
 *      return `{kind: "totp-required", totpSessionToken, ttl}` so the UI
 *      transitions to the TOTP challenge step (mirror of the OTP branch). The
 *      access token is minted later by `verifyTotp` — never here (which would
 *      crash on the absent accessToken).
 *   D) `type: "workspace_required"` — only when the caller asked for it
 *      (`workspaceSelection`): the address belongs to several workspaces. No
 *      token yet; `performWorkspaceSelect` spends `workspaceToken` on the one
 *      the person picks, and its answer is A, B or C.
 * Access tokens NEVER exist pre-OTP/TOTP verification (security boundary).
 *
 * Non-2xx responses throw an ApiError-shaped object so callers can ts-pattern
 * on `errorCode` (AUTH_BAD_CREDENTIALS, TENANT_NOT_FOUND, TENANT_SUSPENDED,
 * ACCOUNT_LOCKED, …).
 */
import { commitVerifiedSession } from "./commitVerifiedSession.js";
import { getAcceptLanguageHeader } from "../refresh/authNetworkConfig.js";
import { sessionTenantSlugStore } from "../tokens/sessionTenantSlugStore.js";
import type { components } from "@linkhr/api-client";
import type { AuthSnapshot, Audience } from "../types.js";

export type WorkspaceItem = components["schemas"]["WorkspaceItem"];

export type LoginResult =
  | { kind: "success"; snapshot: AuthSnapshot }
  | {
      kind: "otp-required";
      otpSessionToken: string;
      ttl: number;
      channel: "email" | "sms";
    }
  // The account has an authenticator app enrolled. Carries the short-lived
  // TOTP-session JWT the UI hands to `verifyTotp` on the challenge step. No
  // access token exists yet (security boundary — minted only on verify).
  | { kind: "totp-required"; totpSessionToken: string; ttl: number }
  // The address has several workspaces and the caller can show a choice. No
  // access token exists yet; `workspaceToken` (valid `ttl` seconds) buys one.
  | {
      kind: "workspace-required";
      workspaceToken: string;
      ttl: number;
      workspaces: WorkspaceItem[];
    };

export interface LoginArgs {
  /** Email or username (`<PREFIX>-<employee number>`), sent as typed. */
  identifier: string;
  password: string;
  /**
   * The company to sign into. Empty on the global entry host, where the user
   * does not know (and should not need) their company: the backend resolves it
   * from the credentials and names it back in `tenantSlug` on the response.
   */
  tenantSlug: string;
  apiBaseUrl: string;
  // Backend tenant-login contract requires `audience` to route the JWT
  // `aud` claim to the right surface (admin | mobile). Missing → 400 with
  // violations[0].field="audience". Injected by `useAuth().login()` from
  // the AuthProvider context, so UI callers never pass this themselves.
  audience: Audience;
  /** Optional. Backend (CC-87) treats absent / null / true as the default
   * 30-day refresh-token TTL; explicit `false` switches to 7-day TTL. */
  rememberMe?: boolean;
  /**
   * The caller can show a workspace choice. Only then may the answer be
   * `workspace-required`; without it an address with several workspaces is
   * refused (409 AUTH_WORKSPACE_SELECTION_REQUIRED).
   */
  workspaceSelection?: boolean;
}

interface LoginSuccessBody {
  type?: string;
  accessToken: string;
  // Honest-nullable: null in cookie-mode (admin) where the refresh token rides
  // an HttpOnly cookie instead of the body.
  refreshToken?: string | null;
  expiresIn?: number;
  tokenType?: string;
  // Real backend LoginResponse is tokens-only; MSW legacy shape includes
  // `user` for back-compat. Treat as optional — derive identity from JWT
  // claims + request-time email below. Phase 3 `GET /me` will hydrate the
  // full profile (fullNameAr/fullNameEn/photoUrl) post-login.
  user?: {
    id: string;
    email?: string | null;
    displayName: string;
    roles?: string[];
    username?: string | null;
  };
  /** True → a temporary password from HR; the token opens only the password change. */
  mustChangePassword?: boolean;
}

interface LoginOtpBody {
  type?: string;
  requiresOtp: true;
  otpSessionToken: string;
  ttl: number;
  channel: "email" | "sms";
}

interface LoginTotpBody {
  type?: string;
  requiresTotp: true;
  totpSessionToken: string;
  ttl: number;
}

type LoginWorkspaceBody = components["schemas"]["LoginWorkspaceRequiredResponse"];

/**
 * A 2xx login response whose shape we don't recognize (unknown discriminator,
 * or a "success" with no access token). Thrown so the flow FAILS CLOSED — no
 * token is stored, no snapshot returned. `AUTH_UNEXPECTED_RESPONSE` maps to the
 * form's generic login error.
 */
function makeUnexpectedResponseError(res: Response): Error {
  return Object.assign(new Error("Login failed"), {
    status: res.status,
    errorCode: "AUTH_UNEXPECTED_RESPONSE",
    correlationId: res.headers.get("x-correlation-id"),
    isApiError: true,
  });
}

export async function performLogin({
  identifier,
  password,
  tenantSlug,
  apiBaseUrl,
  audience,
  rememberMe,
  workspaceSelection,
}: LoginArgs): Promise<LoginResult> {
  const res = await fetch(apiBaseUrl + "/api/v1/auth/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAcceptLanguageHeader() },
    body: JSON.stringify({
      identifier,
      password,
      tenantSlug,
      audience,
      // Only send when the form supplies an explicit choice — backend
      // defaults absent/null to true (30-day TTL).
      ...(rememberMe === false ? { rememberMe: false } : {}),
      ...(workspaceSelection === true ? { workspaceSelectionSupported: true } : {}),
    }),
  });
  return handleLoginResponse(res, { audience, apiBaseUrl, emailHint: identifier });
}

export interface WorkspaceSelectArgs {
  /** From the `workspace-required` answer; single use, expires after its `ttl`. */
  workspaceToken: string;
  tenantId: string;
  apiBaseUrl: string;
  audience: Audience;
}

/**
 * Finish a sign-in in the workspace the person picked
 * (`POST /auth/workspaces:select`). The workspace answers exactly as
 * `/auth/login` does, so the answer goes through the same handling: a
 * session, an OTP or TOTP step, or a refusal (401 AUTH_TOKEN_INVALID for a
 * spent or expired token).
 */
export async function performWorkspaceSelect({
  workspaceToken,
  tenantId,
  apiBaseUrl,
  audience,
}: WorkspaceSelectArgs): Promise<LoginResult> {
  const res = await fetch(apiBaseUrl + "/api/v1/auth/workspaces:select", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAcceptLanguageHeader() },
    body: JSON.stringify({ workspaceToken, tenantId }),
  });
  return handleLoginResponse(res, { audience, apiBaseUrl });
}

export interface IdTokenLoginArgs {
  /**
   * The provider's ID token (JWT): `response.credential` from Google Identity
   * Services, `result.idToken` from MSAL. Never an access token.
   */
  idToken: string;
  audience: Audience;
  rememberMe?: boolean;
  apiBaseUrl: string;
}
export type GoogleLoginArgs = IdTokenLoginArgs;
export type MicrosoftLoginArgs = IdTokenLoginArgs;

/** Sign in with a Google ID token (`POST /auth/google`). */
export function performGoogleLogin(args: GoogleLoginArgs): Promise<LoginResult> {
  return performIdTokenLogin("google", args);
}

/** Sign in with a Microsoft ID token from MSAL (`POST /auth/microsoft`). */
export function performMicrosoftLogin(args: MicrosoftLoginArgs): Promise<LoginResult> {
  return performIdTokenLogin("microsoft", args);
}

/**
 * Sign in with a provider's ID token. No company and no email are sent: the
 * backend reads the address from the token and resolves the company from it,
 * as the global password login does, and answers with `/auth/login`'s own
 * union — so the response goes through the same handling as a password.
 */
async function performIdTokenLogin(
  provider: "google" | "microsoft",
  { idToken, audience, rememberMe, apiBaseUrl }: IdTokenLoginArgs,
): Promise<LoginResult> {
  const res = await fetch(`${apiBaseUrl}/api/v1/auth/${provider}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAcceptLanguageHeader() },
    body: JSON.stringify({
      idToken,
      audience,
      ...(rememberMe === false ? { rememberMe: false } : {}),
    }),
  });
  return handleLoginResponse(res, { audience, apiBaseUrl });
}

/**
 * Everything after the request: the 200 union, the session commit, the
 * company recorded for the next app, and the error mapping. Shared by every
 * way of proving who you are (`/auth/login`, `/auth/google`, `/auth/microsoft`) because their 200
 * bodies and refusals are one contract — one copy, so they cannot drift.
 */
async function handleLoginResponse(
  res: Response,
  {
    audience,
    apiBaseUrl,
    emailHint,
  }: { audience: Audience; apiBaseUrl: string; emailHint?: string },
): Promise<LoginResult> {
  const body = (await res.json().catch(() => ({}))) as
    | LoginSuccessBody
    | LoginOtpBody
    | LoginTotpBody
    | LoginWorkspaceBody
    | { errorCode?: string; title?: string };

  if (!res.ok) {
    const e = body as { errorCode?: string; title?: string };
    // Pick the most specific stable code we can. 429 from gateway/backend
    // often returns an empty body, which would silently fall through to
    // AUTH_BAD_CREDENTIALS — that's why rate-limited login attempts were
    // showing "email or password is incorrect" instead of a rate-limit
    // banner. Detect by status when the body is opaque.
    const explicitCode = e.errorCode;
    const fallbackCode =
      res.status === 429
        ? "AUTH_RATE_LIMITED"
        : res.status === 423
          ? "ACCOUNT_LOCKED"
          : res.status === 401 || res.status === 400
            ? "AUTH_BAD_CREDENTIALS"
            : "UNKNOWN_ERROR";
    const retryAfterRaw = res.headers.get("retry-after");
    const retryAfterSeconds =
      retryAfterRaw !== null && retryAfterRaw !== "" && /^\d+$/.test(retryAfterRaw)
        ? Number.parseInt(retryAfterRaw, 10)
        : undefined;
    throw Object.assign(new Error(e.title ?? "Login failed"), {
      // The RFC 7807 title as a real property — consumers branch on it
      // (message alone is not introspectable as "the server copy").
      title: e.title,
      status: res.status,
      errorCode: explicitCode ?? fallbackCode,
      correlationId: res.headers.get("x-correlation-id"),
      retryAfterSeconds,
      isApiError: true,
    });
  }

  // Every 200 shape (success / otp_required / totp_required) names the company
  // the credentials belong to. Record it: on the global entry host the address
  // does not, and `resolveTenant` has nothing else to read. Written BEFORE the
  // dispatch below so the MFA steps and the app that boots after them all see
  // it. Keyed by the audience actually being signed in with, which the global
  // login decides per attempt.
  sessionTenantSlugStore.set((body as { tenantSlug?: string | null }).tenantSlug, audience);

  // 200 tagged union (discriminator `type`). Fail closed: dispatch explicitly
  // on `type` and only build an authenticated snapshot for an explicit
  // "success" carrying a valid access token. An unknown/absent discriminator
  // (or a malformed success body with no token) must NEVER yield a session.
  const bodyType = (body as { type?: string }).type;
  switch (bodyType) {
    case "otp_required": {
      const otp = body as LoginOtpBody;
      return {
        kind: "otp-required",
        otpSessionToken: otp.otpSessionToken,
        ttl: otp.ttl,
        channel: otp.channel,
      };
    }
    case "totp_required": {
      // The account has an authenticator app enrolled; there is no access token
      // here — the UI transitions to the TOTP challenge step and `verifyTotp`
      // mints the token after a valid code / recovery code.
      const totp = body as LoginTotpBody;
      return { kind: "totp-required", totpSessionToken: totp.totpSessionToken, ttl: totp.ttl };
    }
    case "workspace_required": {
      const choice = body as LoginWorkspaceBody;
      return {
        kind: "workspace-required",
        workspaceToken: choice.workspaceToken,
        ttl: choice.ttl,
        workspaces: choice.workspaces,
      };
    }
    case "success":
      break; // fall through to the token handling below
    default:
      // Anything else — an UNKNOWN discriminator OR an ABSENT one — must NEVER
      // authenticate. The contract requires `type` on the 200 (schema.d.ts) and
      // every server/mock emits it; a missing/unknown discriminator is a
      // malformed response, not a session.
      throw makeUnexpectedResponseError(res);
  }

  // Response A — standard success. Route through the shared security boundary:
  // it validates the token (decodable, unexpired, correct audience, has a
  // subject) BEFORE storing anything, and throws AUTH_UNEXPECTED_RESPONSE on a
  // garbage / expired / wrong-audience / token-less body. `tid`/`tenant_id` in
  // the JWT drive the snapshot tenantId. A `mustChangePassword` success comes
  // back with `snapshot.mustChangePassword: true`: the caller sends the person
  // to the password change instead of the redirect target.
  const success = body as LoginSuccessBody;
  const snapshot = await commitVerifiedSession({
    accessToken: success.accessToken,
    expectedAudience: audience,
    apiBaseUrl,
    bodyUser: success.user,
    emailHint,
    mustChangePassword: success.mustChangePassword === true,
    correlationId: res.headers.get("x-correlation-id"),
  });
  return { kind: "success", snapshot };
}

/**
 * Run a sign-in, and when the backend refuses the surface (`403
 * AUTH_ADMIN_AUDIENCE_NOT_PERMITTED` — the proof was right, the audience was
 * not) ask `nextAudience` where to go instead and send the SAME proof again.
 * The refusal arrives before any MFA step, so nothing is half-started.
 *
 * Ends when `nextAudience` says `null` or names an audience already tried —
 * the last error is rethrown, so the caller shows one message and never loops.
 */
export async function loginWithAudienceFallback(
  call: (audience: Audience | undefined) => Promise<LoginResult>,
  audience: Audience | undefined,
  nextAudience?: (attempted: Audience) => Audience | null,
): Promise<LoginResult> {
  const tried = new Set<Audience | undefined>();
  let current = audience;
  for (;;) {
    tried.add(current);
    try {
      return await call(current);
    } catch (err) {
      const code = (err as { errorCode?: unknown } | null)?.errorCode;
      if (code !== "AUTH_ADMIN_AUDIENCE_NOT_PERMITTED") throw err;
      const next = nextAudience?.(current ?? "admin") ?? null;
      if (next === null || tried.has(next)) throw err;
      current = next;
    }
  }
}
